import * as React from 'react';
import { UploadLine, CloseLine, FileLine } from '@mingcute/react';

import { cn } from '../lib/cn';

import { Progress } from './progress';

export interface UploadedFile {
  file: File;
  progress?: number;
  status?: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

export interface FileUploadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  accept?: string;
  multiple?: boolean;
  maxSize?: number;
  maxFiles?: number;
  files?: UploadedFile[];
  onChange?: (files: File[]) => void;
  onRemove?: (index: number) => void;
  variant?: 'dropzone' | 'button';
  invalid?: boolean;
  disabled?: boolean;
  hint?: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const FileUpload = React.forwardRef<HTMLInputElement, FileUploadProps>(
  (
    {
      accept,
      multiple = true,
      maxSize,
      maxFiles,
      files = [],
      onChange,
      onRemove,
      variant = 'dropzone',
      invalid,
      disabled,
      hint,
      className,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [isDragging, setIsDragging] = React.useState(false);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const validateFiles = (incoming: FileList | File[]): File[] => {
      let arr = Array.from(incoming);
      if (maxSize) arr = arr.filter((f) => f.size <= maxSize);
      if (maxFiles) arr = arr.slice(0, maxFiles - files.length);
      return arr;
    };

    const handleFiles = (incoming: FileList | File[]) => {
      const valid = validateFiles(incoming);
      if (valid.length > 0) onChange?.(valid);
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);
      if (disabled) return;
      handleFiles(e.dataTransfer.files);
    };

    if (variant === 'button') {
      return (
        <div className={cn('flex flex-col gap-2', className)} {...props}>
          <button
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
            className={cn(
              'inline-flex h-9 items-center justify-center gap-2 rounded-[var(--radius-md)] border px-4 text-sm font-medium',
              'border-[var(--color-border)] bg-[var(--color-surface-2)] text-[var(--color-fg)] hover:bg-[var(--color-subtle)]',
              'focus-visible:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-60',
            )}
          >
            <UploadLine size={16} /> Choose files
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={disabled}
            className="sr-only"
            onChange={(e) => e.target.files && handleFiles(e.target.files)}
          />
          {files.length > 0 ? <FileList files={files} onRemove={onRemove} /> : null}
          {hint ? <p className="text-xs text-[var(--color-fg-muted)]">{hint}</p> : null}
        </div>
      );
    }

    return (
      <div className={cn('flex flex-col gap-2', className)} {...props}>
        <div
          role="button"
          data-pop="false"
          tabIndex={disabled ? -1 : 0}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) inputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          aria-invalid={invalid || undefined}
          className={cn(
            'flex flex-col items-center justify-center gap-2 rounded-[var(--radius-md)] border-2 border-dashed p-6 text-center text-sm',
            'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
            isDragging
              ? 'border-[var(--color-accent)] bg-[var(--color-accent-subtle)]'
              : invalid
              ? 'border-[var(--color-danger)] bg-[var(--color-surface)]'
              : 'border-[var(--color-border)] bg-[var(--color-surface)]',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          <UploadLine size={24} className="text-[var(--color-fg-muted)]" />
          <div className="text-[var(--color-fg)]">
            <span className="font-medium text-[var(--color-accent)]">Click to upload</span>{' '}
            or drag and drop
          </div>
          {hint ? <div className="text-xs text-[var(--color-fg-muted)]">{hint}</div> : null}
          {maxSize ? (
            <div className="text-[10px] text-[var(--color-fg-muted)]">
              Max {formatBytes(maxSize)} per file
            </div>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          className="sr-only"
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
        {files.length > 0 ? <FileList files={files} onRemove={onRemove} /> : null}
      </div>
    );
  },
);
FileUpload.displayName = 'FileUpload';

interface FileListProps {
  files: UploadedFile[];
  onRemove?: (index: number) => void;
}

const FileList: React.FC<FileListProps> = ({ files, onRemove }) => (
  <ul className="flex flex-col gap-1.5">
    {files.map((f, i) => (
      <li
        key={`${f.file.name}-${i}`}
        className="flex items-center gap-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1.5"
      >
        <FileLine size={16} className="shrink-0 text-[var(--color-fg-muted)]" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs text-[var(--color-fg)]">{f.file.name}</div>
          {f.status === 'uploading' && typeof f.progress === 'number' ? (
            <Progress value={f.progress} size="sm" className="mt-1" />
          ) : (
            <div className="text-[10px] text-[var(--color-fg-muted)]">
              {formatBytes(f.file.size)}
              {f.status === 'error' ? ` • ${f.error ?? 'Failed'}` : null}
            </div>
          )}
        </div>
        {onRemove ? (
          <button
            type="button"
            aria-label="Remove file"
            onClick={() => onRemove(i)}
            className="shrink-0 rounded-full p-0.5 text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]"
          >
            <CloseLine size={14} />
          </button>
        ) : null}
      </li>
    ))}
  </ul>
);
